import { environment as sharedLocal } from './environment';
import { environment as sharedDemo } from './environment.demo';
import { environment as sharedPentest } from './environment.pentest';
import { environment as sharedProduction } from './environment.prod';
import { environment as sharedStaging } from './environment.staging';

import { environment as localLogin } from './login/environment';
import { environment as demoLogin } from './login/environment.demo';
import { environment as pentestLogin } from './login/environment.pentest';
import { environment as productionLogin } from './login/environment.prod';
import { environment as stagingLogin } from './login/environment.staging';

import { environment as localPortfolio } from './portfolio/environment';
import { environment as demoPortfolio } from './portfolio/environment.demo';
import { environment as pentestPortfolio } from './portfolio/environment.pentest';
import { environment as productionPortfolio } from './portfolio/environment.prod';
import { environment as stagingPortfolio } from './portfolio/environment.staging';

import { environment as localWorkspace } from './workspace/environment';
import { environment as demoWorkspace } from './workspace/environment.demo';
import { environment as productionWorkspace } from './workspace/environment.prod';
import { environment as stagingWorkspace } from './workspace/environment.staging';

import { environment as localSupport } from './support/environment';
import { environment as demoSupport } from './support/environment.demo';
import { environment as pentestSupport } from './support/environment.pentest';
import { environment as productionSupport } from './support/environment.prod';
import { environment as stagingSupport } from './support/environment.staging';

import { environment as localApp } from './portfolio-universal-app/environment';
import { environment as demoApp } from './portfolio-universal-app/environment.demo';
import { environment as productionApp } from './portfolio-universal-app/environment.prod';
import { environment as stagingApp } from './portfolio-universal-app/environment.staging';

const sharedSettings = {
  // Local settings
  local: {
    ...sharedLocal,
  },

  // Demo settings
  demo: {
    ...sharedDemo,
  },

  // Staging settings
  staging: {
    ...sharedStaging,
  },

  // Prod settings
  production: {
    ...sharedProduction,
  },

  // Pentest settings
  pentest: {
    ...sharedPentest,
  },
};

export interface EnvironmentSettings {
  environment: string;
  production: boolean;
  devMode: boolean;
  ssoAllowed: boolean;

  version: string;
  applicationName?: string;

  authAppURL: string;
  authApiURL: string;

  portfolioClientName: string;
  portfolioAppURL: string;
  portfolioApiURL: string;

  aiApiUrl: string;

  workspaceAppURL: string;
  workspaceApiURL: string;

  nl: DocumentationKeys;
  en: DocumentationKeys;

  // the authenticator title for this specific environment
  otpIssuer: string;
  otpBannerEnabled: boolean;

  environmentIndicator: {
    color?: string;
    text?: string;
    icon?: string;
  };

  bugsnagApiKey?: string;

  // TODO: Add app specific settings here.
  authRedirectURI?: string;
  authScope?: string;
  authRedirectSource?: string;
  authAppId?: string;
  uthRedirectURI?: string;
  canChangeBE?: boolean;
}

export interface DocumentationKeys {
  gettingStartedUrl?: string;
  userManagementUrl?: string;
  accessManagementUrl?: string;
  faqLoginUrl?: string;
}

export const environments = {
  // Local environments
  'localhost:4200': {
    ...sharedSettings.local,
    ...localLogin,
  },
  'localhost:4201': {
    ...sharedSettings.local,
    ...localPortfolio,
  },
  'localhost:4202': {
    ...sharedSettings.local,
    ...localSupport,
  },
  'localhost:4203': {
    ...sharedSettings.local,
    ...localApp,
  },
  'localhost:4204': {
    ...sharedSettings.local,
    ...localWorkspace,
  },
  // specific address used by android simulator
  '10.0.2.2:4203': {
    ...sharedSettings.local,
    ...localApp,
  },

  // Demo environments
  'logindemo.reconcept.nl': {
    ...sharedSettings.demo,
    ...demoLogin,
  },
  'portfoliodemo.reconcept.nl': {
    ...sharedSettings.demo,
    ...demoPortfolio,
  },
  'workspacedemo.reconcept.nl': {
    ...sharedSettings.demo,
    ...demoWorkspace,
  },
  'supportdemo.reconcept.nl': {
    ...sharedSettings.demo,
    ...demoSupport,
  },
  'appdemo.reconcept.nl': {
    ...sharedSettings.demo,
    ...demoApp,
  },
  'reconcept.demo.app': {
    ...sharedSettings.demo,
    ...demoApp,
  },

  // Staging environments
  'loginstaging.reconcept.nl': {
    ...sharedSettings.staging,
    ...stagingLogin,
  },
  'portfoliostaging.reconcept.nl': {
    ...sharedSettings.staging,
    ...stagingPortfolio,
  },
  'workspacestaging.reconcept.nl': {
    ...sharedSettings.staging,
    ...stagingWorkspace,
  },
  'supportstaging.reconcept.nl': {
    ...sharedSettings.staging,
    ...stagingSupport,
  },
  'appstaging.reconcept.nl': {
    ...sharedSettings.staging,
    ...stagingApp,
  },

  // Prod environments
  'login.reconcept.nl': {
    ...sharedSettings.production,
    ...productionLogin,
  },
  'portfolio.reconcept.nl': {
    ...sharedSettings.production,
    ...productionPortfolio,
  },
  'workspace.reconcept.nl': {
    ...sharedSettings.production,
    ...productionWorkspace,
  },
  'support.reconcept.nl': {
    ...sharedSettings.production,
    ...productionSupport,
  },

  'reconcept.app': {
    ...sharedSettings.production,
    ...productionApp,
  },

  // Pentest environments
  'loginpentest.reconcept.nl': {
    ...sharedSettings.pentest,
    ...pentestLogin,
  },
  'portfoliopentest.reconcept.nl': {
    ...sharedSettings.pentest,
    ...pentestPortfolio,
  },
  'supportpentest.reconcept.nl': {
    ...sharedSettings.pentest,
    ...pentestSupport,
  },

  // bunny production test
  'reconcept-portfolio-production.b-cdn.net': {
    ...sharedSettings.production,
    ...productionPortfolio,
    authAppURL: 'https://reconcept-login-production.b-cdn.net',
    authRedirectURI: 'https://reconcept-portfolio-production.b-cdn.net/login/success',
  },
  'reconcept-support-production.b-cdn.net': {
    ...sharedSettings.production,
    ...productionSupport,
    authAppURL: 'https://reconcept-login-production.b-cdn.net',
    authRedirectURI: 'https://reconcept-portfolio-production.b-cdn.net/login/success',
  },
  'reconcept-workspace-production.b-cdn.net': {
    ...sharedSettings.production,
    ...productionWorkspace,
    authAppURL: 'https://reconcept-login-production.b-cdn.net',
    authRedirectURI: 'https://reconcept-portfolio-production.b-cdn.net/login/success',
  },
  'reconcept-login-production.b-cdn.net': {
    ...sharedSettings.production,
    ...productionLogin,
    authAppURL: 'https://reconcept-login-production.b-cdn.net',
    authRedirectURI: 'https://reconcept-portfolio-production.b-cdn.net/login/success',
  },
};

export type EnvironmentHost = keyof typeof environments;

export function isValidEnvironmentHost(value: any): value is EnvironmentHost {
  return Object.keys(environments).includes(value);
}
